import * as React from "react";
import _ from "lodash";

import { IEliminationGrid, ILeagueMember, ITeamInfo } from "../../models/interfaces";
import { teamLogoStyle, teamIdForName } from "../../services/teamLogoService";

/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid, no-script-url */

export interface ILeagueEliminationSeasonSummaryProps {
    eliminationGrid: Array<IEliminationGrid>;
    leagueMembers: Array<ILeagueMember>;
    teams: Array<ITeamInfo> | null;
    // LeagueRuleSet.AllowedSeasonMisses: lost picks a player survives before being eliminated
    // (0 for the pure elimination rule sets, 1 for "Double Elimination (1 miss)")
    allowedSeasonMisses?: number;
}

// Grid cell statuses (see LeagueMemberPickView): "Team|0" in progress, "Team|1" lost (a miss), "Team|2" won.
const STATUS_MISS = "1";
const STATUS_SUCCESS = "2";

interface IPick {
    team: string;
    status: string;
}

interface ICount {
    label: string;
    count: number;
}

interface IWeekSummary {
    weekIndex: number;
    players: number;
    eliminated: number;
    // at least one pick in the week has a result (the week is in the table either way)
    resolved: boolean;
    byTeam: ICount[];
}

interface ITeamSummary {
    team: string;
    eliminated: number;
    byWeek: ICount[];
}

interface ISeasonSummary {
    started: boolean;
    players: number;
    remaining: number;
    eliminated: number;
    weeks: IWeekSummary[];
    teams: ITeamSummary[];
    usedByRemaining: ICount[];
}

// a cell may hold several picks separated by ';' (multi-pick rule sets)
const parseCell = (cell: string | null | undefined): IPick[] => {
    if (_.isNil(cell) || cell.length === 0) {
        return [];
    }
    return cell.split(';')
        .map(p => p.split('|'))
        .filter(parts => parts.length === 2)
        .map(parts => ({ team: parts[0], status: parts[1] }));
};

const sortCounts = (counts: ICount[]): ICount[] =>
    counts.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

const weekLabel = (weekIndex: number): string => `Week ${weekIndex + 1}`;

const toCounts = (counts: { [label: string]: number }): ICount[] =>
    Object.keys(counts).map(label => ({ label, count: counts[label] }));

// The pick that knocks a member out: the first miss past the rule set's allowance, walking their picks in
// week order (misses inside one week count in cell order). Null while they are still alive. Picks after
// `lastWeek` are ignored.
const knockoutPick = (weeks: string[], allowedSeasonMisses: number, lastWeek: number): { weekIndex: number; team: string } | null => {
    let misses = 0;
    for (let weekIndex = 0; weekIndex < weeks.length && weekIndex <= lastWeek; weekIndex++) {
        for (const pick of parseCell(weeks[weekIndex])) {
            if (pick.status === STATUS_MISS && ++misses > allowedSeasonMisses) {
                return { weekIndex, team: pick.team };
            }
        }
    }
    return null;
};

export const buildSummary = (grid: Array<IEliminationGrid>, members: Array<ILeagueMember>, allowedSeasonMisses: number = 0): ISeasonSummary => {
    const weekCount = _.max(grid.map(g => (g.weeks || []).length)) || 0;

    const weekElims: { [weekIndex: number]: { [team: string]: number } } = {};
    const weekPlayers: { [weekIndex: number]: number } = {};
    const weekResolved: { [weekIndex: number]: boolean } = {};
    // a week with any pick still waiting on a result (or with games not yet locked) is incomplete
    const weekPending: { [weekIndex: number]: boolean } = {};
    // per member: knocked out, and the week it happened in (-1 when only the Eliminated flag says so)
    const memberStatus: Array<{ eliminated: boolean; knockoutWeek: number }> = [];
    const teamElims: { [team: string]: { [weekIndex: number]: number } } = {};
    const usedByRemaining: { [team: string]: number } = {};

    let started = false;
    let eliminatedCount = 0;
    let remainingCount = 0;

    // Members drive the counts; the grid supplies the detail. A member with no locked picks yet has no
    // grid row, and the grid can hold rows for deleted members, which are ignored.
    const rowFor = (leagueMemberId: number): string[] => {
        const row = _.find(grid, g => g.leagueMemberId === leagueMemberId);
        return (row && row.weeks) || [];
    };

    const knockoutWeek = (weeks: string[], lastWeek: number = Number.MAX_SAFE_INTEGER): number => {
        const knockout = knockoutPick(weeks, allowedSeasonMisses, lastWeek);
        return knockout === null ? -1 : knockout.weekIndex;
    };

    const lastPickWeek = (weeks: string[]): number =>
        _.findLastIndex(weeks, cell => parseCell(cell).length > 0);

    // Nothing after the week the league was decided is considered: picks in later weeks are noise.
    // With declared winner(s), that is the winners' last pick week; otherwise it is the week all but
    // one player had been knocked out.
    const winners = members.filter(m => m.winner === true);
    let decidedWeek = Number.MAX_SAFE_INTEGER;
    if (winners.length > 0) {
        // co-winners (tied) share the deciding week: the last week any of them picked
        const week = _.max(winners.map(w => lastPickWeek(rowFor(w.leagueMemberId)))) as number;
        if (week >= 0) {
            decidedWeek = week;
        }
    } else if (members.length >= 2) {
        const knockoutWeeks = members.map(m => knockoutWeek(rowFor(m.leagueMemberId))).filter(w => w >= 0).sort((a, b) => a - b);
        if (knockoutWeeks.length >= members.length - 1) {
            decidedWeek = knockoutWeeks[members.length - 2];
        }
    }

    members.forEach(member => {
        const weeks = rowFor(member.leagueMemberId);
        const knockout = knockoutPick(weeks, allowedSeasonMisses, decidedWeek);
        const wins: string[] = [];

        weeks.forEach((cell, weekIndex) => {
            if (weekIndex > decidedWeek) {
                return;
            }
            const picks = parseCell(cell);
            if (picks.length === 0) {
                return;
            }
            weekPlayers[weekIndex] = (weekPlayers[weekIndex] || 0) + 1;

            picks.forEach(pick => {
                if (pick.status === STATUS_MISS || pick.status === STATUS_SUCCESS) {
                    started = true;
                    weekResolved[weekIndex] = true;
                } else {
                    weekPending[weekIndex] = true;
                }
                if (pick.status === STATUS_SUCCESS) {
                    wins.push(pick.team);
                }
            });
        });

        // only the miss that took the member past the allowance is an elimination; earlier misses
        // (rule sets that allow some) are survived and do not appear in the week/team tallies
        if (knockout !== null) {
            const { weekIndex, team } = knockout;
            weekElims[weekIndex] = weekElims[weekIndex] || {};
            weekElims[weekIndex][team] = (weekElims[weekIndex][team] || 0) + 1;
            teamElims[team] = teamElims[team] || {};
            teamElims[team][weekIndex] = (teamElims[team][weekIndex] || 0) + 1;
        }

        // the Eliminated flag is authoritative, but the grid may know about a loss before the flag is set;
        // the league winner always counts as remaining, even if they lost in the deciding week (and won
        // on the tiebreak) - that loss still shows up in the week/team elimination tallies above
        const eliminated = member.winner === true
            ? false
            : knockout !== null || member.eliminated === true;
        if (eliminated) {
            eliminatedCount++;
        } else {
            remainingCount++;
            wins.forEach(team => {
                usedByRemaining[team] = (usedByRemaining[team] || 0) + 1;
            });
        }
        memberStatus.push({ eliminated, knockoutWeek: knockoutWeek(weeks) });
    });

    // players still in the league going into a week: everyone not yet knocked out before it
    const aliveEntering = (weekIndex: number): number =>
        memberStatus.filter(m => !m.eliminated || m.knockoutWeek >= weekIndex).length;

    // the latest week with any locked picks is the week being played (or the last one played)
    const lastWeekWithPicks = _.max(Object.keys(weekPlayers).map(Number)) || 0;

    const weeks: IWeekSummary[] = [];
    for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
        if (!weekPlayers[weekIndex]) {
            continue;
        }
        const byTeam = sortCounts(toCounts(weekElims[weekIndex] || {}));
        // While a week is still being played only the locked picks are visible in the grid, so the pick
        // count undercounts the field; use the players who entered the week instead. Once a week is over
        // (every pick has a result and a later week has started) the pick count is exact, and allows
        // for members who joined mid-season.
        const complete = !weekPending[weekIndex] && weekIndex < lastWeekWithPicks;
        const players = complete
            ? weekPlayers[weekIndex]
            : Math.max(weekPlayers[weekIndex], aliveEntering(weekIndex));
        weeks.push({
            weekIndex,
            players,
            eliminated: _.sumBy(byTeam, t => t.count),
            resolved: weekResolved[weekIndex] === true,
            byTeam
        });
    }

    const teams: ITeamSummary[] = Object.keys(teamElims).map(team => {
        const byWeekMap = teamElims[team];
        const byWeek: ICount[] = Object.keys(byWeekMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map(weekIndex => ({ label: weekLabel(weekIndex), count: byWeekMap[weekIndex] }));
        return {
            team,
            eliminated: _.sumBy(byWeek, w => w.count),
            byWeek
        };
    }).sort((a, b) => b.eliminated - a.eliminated || a.team.localeCompare(b.team));

    return {
        started,
        players: members.length,
        remaining: remainingCount,
        eliminated: eliminatedCount,
        weeks,
        teams,
        usedByRemaining: sortCounts(toCounts(usedByRemaining))
    };
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`;

const bar = (value: number, max: number, className: string): JSX.Element => {
    const percent = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
        <div className="summary-bar">
            <div className={`summary-bar-fill ${className}`} style={{ width: `${percent}%` }} />
        </div>
    );
};

const chips = (counts: ICount[], className: string): JSX.Element | null => {
    if (counts.length === 0) {
        return <span className="summary-muted">nobody</span>;
    }
    return (
        <span>
            {counts.map(c =>
                <span key={c.label} className={`summary-chip ${className}`}>
                    {c.label}<span className="summary-chip-count">&times;{c.count}</span>
                </span>
            )}
        </span>
    );
};

const statTile = (value: number | string, label: string, className: string): JSX.Element => (
    <div className={`summary-tile ${className}`}>
        <div className="summary-tile-value">{value}</div>
        <div className="summary-tile-label">{label}</div>
    </div>
);

const weekTable = (summary: ISeasonSummary): JSX.Element => {
    return (
        <table className="table table-bordered table-hover shadow summary-table">
            <thead>
                <tr>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="Week of the season."><i className="fa fa-calendar" aria-hidden="true"/> Week</a></th>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="Players eliminated this week, out of the players who had a pick."><i className="fa fa-close" aria-hidden="true"/> Eliminated</a></th>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="Which teams did the eliminating, and how many players each took out."><i className="fa fa-shield" aria-hidden="true"/> Knocked out by</a></th>
                </tr>
            </thead>
            <tbody>
                {summary.weeks.map(w =>
                    <tr key={w.weekIndex}>
                        <td className="no-wrap"><b>{weekLabel(w.weekIndex)}</b></td>
                        <td className="summary-count-cell">
                            <span className={w.eliminated > 0 ? "grid-eliminated" : "summary-muted"}>{w.eliminated}</span>
                            <span className="summary-muted"> / {w.players}</span>
                            {bar(w.eliminated, w.players, "summary-bar-red")}
                        </td>
                        <td>{chips(w.byTeam, "summary-chip-red")}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

const teamCell = (teamName: string, teams: Array<ITeamInfo> | null): JSX.Element => (
    <td className="no-wrap team-logo-cell" style={teamLogoStyle(teamIdForName(teams, teamName))}><b>{teamName}</b></td>
);

const teamTable = (summary: ISeasonSummary, teams: Array<ITeamInfo> | null): JSX.Element => {
    const max = _.max(summary.teams.map(t => t.eliminated)) || 0;
    return (
        <table className="table table-bordered table-hover shadow summary-table">
            <thead>
                <tr>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="NFL team that was picked and lost."><i className="fa fa-shield" aria-hidden="true"/> Team</a></th>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="Total players this team has eliminated."><i className="fa fa-close" aria-hidden="true"/> Eliminated</a></th>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="Which weeks the eliminations happened in."><i className="fa fa-calendar" aria-hidden="true"/> When</a></th>
                </tr>
            </thead>
            <tbody>
                {summary.teams.length === 0 &&
                    <tr><td colSpan={3}>No eliminations yet. Everyone is still alive!</td></tr>}
                {summary.teams.map(t =>
                    <tr key={t.team}>
                        {teamCell(t.team, teams)}
                        <td className="summary-count-cell">
                            <span className="grid-eliminated">{t.eliminated}</span>
                            {bar(t.eliminated, max, "summary-bar-red")}
                        </td>
                        <td>{chips(t.byWeek, "summary-chip-red")}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

const usedTable = (summary: ISeasonSummary, teams: Array<ITeamInfo> | null): JSX.Element => {
    return (
        <table className="table table-bordered table-hover shadow summary-table">
            <thead>
                <tr>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="NFL team that was picked and won."><i className="fa fa-shield" aria-hidden="true"/> Team</a></th>
                    <th><a className="tooltip-anchor" href="javascript:" data-toggle="tooltip" data-placement="top" title="How many of the remaining players have already used (and can no longer pick) this team."><i className="fa fa-check-square-o" aria-hidden="true"/> Used by remaining players</a></th>
                </tr>
            </thead>
            <tbody>
                {summary.usedByRemaining.length === 0 &&
                    <tr><td colSpan={2}>No winning picks by remaining players yet.</td></tr>}
                {summary.usedByRemaining.map(u =>
                    <tr key={u.label}>
                        {teamCell(u.label, teams)}
                        <td className="summary-count-cell">
                            <span className="grid-success">{u.count}</span>
                            <span className="summary-muted"> of {summary.remaining}</span>
                            {bar(u.count, summary.remaining, "summary-bar-green")}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

const LeagueEliminationSeasonSummary = (props: ILeagueEliminationSeasonSummaryProps): JSX.Element => {
    const allowedSeasonMisses = props.allowedSeasonMisses || 0;
    const summary = React.useMemo(
        () => buildSummary(props.eliminationGrid || [], props.leagueMembers || [], allowedSeasonMisses),
        [props.eliminationGrid, props.leagueMembers, allowedSeasonMisses]);

    if (!summary.started) {
        return <p>No results yet</p>;
    }

    // a week with only in-progress picks is listed in the table but has not been "played" yet
    const weeksPlayed = summary.weeks.filter(w => w.resolved).length;

    return (
        <div>
            <div className="summary-tiles">
                {statTile(summary.players, "Players", "")}
                {statTile(summary.remaining, "Remaining", "summary-tile-green")}
                {statTile(summary.eliminated, "Eliminated", "summary-tile-red")}
                {statTile(weeksPlayed, weeksPlayed === 1 ? "Week Played" : "Weeks Played", "")}
            </div>

            <div className="row">
                <div className="col-md-6">
                    <h4 className="summary-heading"><i className="fa fa-calendar" aria-hidden="true"/> Week by Week</h4>
                    {weekTable(summary)}
                </div>
                <div className="col-md-6">
                    <h4 className="summary-heading"><i className="fa fa-shield" aria-hidden="true"/> Team by Team</h4>
                    {teamTable(summary, props.teams)}
                </div>
            </div>

            <h4 className="summary-heading"><i className="fa fa-check-square-o" aria-hidden="true"/> Teams Already Used by Remaining Players</h4>
            <p className="summary-muted">
                The {plural(summary.remaining, "remaining player")} can't pick these teams again. The higher the count, the more picks are being funneled toward whatever is left.
            </p>
            {usedTable(summary, props.teams)}
        </div>
    );
};

export default LeagueEliminationSeasonSummary;
